/// Zustand store for engine state: deployed processes, running instances, jobs,
/// user tasks, worker status, and event log. Updated reactively by WorkerManager events.

import { create } from "zustand";
import type {
  DeployedProcess,
  DeployedNodeFlow,
  ProcessInstance,
  Job,
  EngineEventLog,
  WorkerInfo,
  InstanceState,
  JobState,
} from "@/types/engine";

// ---------------------------------------------------------------------------
// User Task type (used by TaskPanel / useUserTasks)
// ---------------------------------------------------------------------------

export interface UserTask {
  jobKey: string;
  formKey: string;
  taskName: string;
  variables: Record<string, unknown>;
  customHeaders: Record<string, unknown>;
  assignee?: string;
  candidateGroups?: string[];
  activatedAt: number;
}

// ---------------------------------------------------------------------------
// Store state shape
// ---------------------------------------------------------------------------

interface EngineStoreState {
  /** Registered engine workers and their status. */
  workers: Map<string, WorkerInfo>;
  /** Deployed BPMN process definitions. */
  deployedProcesses: DeployedProcess[];
  /** Deployed node flow definitions. */
  deployedNodeFlows: DeployedNodeFlow[];
  /** Running/completed process instances. */
  runningInstances: ProcessInstance[];
  /** Known jobs. */
  jobs: Job[];
  /** Event log (most recent first). */
  events: EngineEventLog[];
  /** Maximum event log entries to keep. */
  maxEvents: number;
  /** Active user tasks awaiting form completion. */
  userTasks: UserTask[];
  /** Currently selected user task job key. */
  activeTaskJobKey: string | null;
}

interface EngineStoreActions {
  // Worker lifecycle
  setWorkerStatus: (
    workerId: string,
    role: string,
    status: WorkerInfo["status"],
    error?: string,
  ) => void;
  removeWorker: (workerId: string) => void;

  // Deploy
  addDeployedProcess: (process: DeployedProcess) => void;
  addDeployedNodeFlow: (flow: DeployedNodeFlow) => void;

  // Instance lifecycle
  addInstance: (instance: ProcessInstance) => void;
  updateInstanceState: (instanceKey: string, state: InstanceState) => void;
  updateInstanceVariables: (instanceKey: string, variables: Record<string, unknown>) => void;
  updateInstanceElement: (instanceKey: string, elementId: string) => void;

  // Job lifecycle
  addJob: (job: Job) => void;
  updateJobState: (jobKey: string, state: JobState, extra?: Partial<Job>) => void;

  // Event log
  pushEvent: (event: EngineEventLog) => void;
  clearEvents: () => void;

  // User task lifecycle
  addUserTask: (task: UserTask) => void;
  removeUserTask: (jobKey: string) => void;
  setActiveTask: (jobKey: string | null) => void;

  // Handle raw engine events from WorkerManager
  handleEngineEvent: (
    workerId: string,
    eventType: string,
    payload: Record<string, unknown>,
  ) => void;

  // Reset
  reset: () => void;
}

type EngineStore = EngineStoreState & EngineStoreActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: EngineStoreState = {
  workers: new Map(),
  deployedProcesses: [],
  deployedNodeFlows: [],
  runningInstances: [],
  jobs: [],
  events: [],
  maxEvents: 1000,
  userTasks: [],
  activeTaskJobKey: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useEngineStore = create<EngineStore>((set, get) => ({
  ...initialState,

  // -------------------------------------------------------------------------
  // Worker lifecycle
  // -------------------------------------------------------------------------

  setWorkerStatus: (workerId, role, status, error) =>
    set((state) => {
      const workers = new Map(state.workers);
      const existing = workers.get(workerId);
      workers.set(workerId, {
        workerId,
        role: existing?.role ?? role,
        status,
        error,
        readyAt: status === "ready" ? Date.now() : existing?.readyAt,
      });
      return { workers };
    }),

  removeWorker: (workerId) =>
    set((state) => {
      const workers = new Map(state.workers);
      workers.delete(workerId);
      return { workers };
    }),

  // -------------------------------------------------------------------------
  // Deploy
  // -------------------------------------------------------------------------

  addDeployedProcess: (process) =>
    set((state) => ({
      deployedProcesses: [...state.deployedProcesses, process],
    })),

  addDeployedNodeFlow: (flow) =>
    set((state) => ({
      deployedNodeFlows: [...state.deployedNodeFlows, flow],
    })),

  // -------------------------------------------------------------------------
  // Instance lifecycle
  // -------------------------------------------------------------------------

  addInstance: (instance) =>
    set((state) => ({
      runningInstances: [...state.runningInstances, instance],
    })),

  updateInstanceState: (instanceKey, newState) =>
    set((state) => ({
      runningInstances: state.runningInstances.map((inst) =>
        inst.instanceKey === instanceKey
          ? {
              ...inst,
              state: newState,
              completedAt:
                newState === "completed" || newState === "failed" ? Date.now() : inst.completedAt,
            }
          : inst,
      ),
    })),

  updateInstanceVariables: (instanceKey, variables) =>
    set((state) => ({
      runningInstances: state.runningInstances.map((inst) =>
        inst.instanceKey === instanceKey ? { ...inst, variables } : inst,
      ),
    })),

  updateInstanceElement: (instanceKey, elementId) =>
    set((state) => ({
      runningInstances: state.runningInstances.map((inst) =>
        inst.instanceKey === instanceKey ? { ...inst, currentElement: elementId } : inst,
      ),
    })),

  // -------------------------------------------------------------------------
  // Job lifecycle
  // -------------------------------------------------------------------------

  addJob: (job) =>
    set((state) => ({
      jobs: [...state.jobs, job],
    })),

  updateJobState: (jobKey, newState, extra) =>
    set((state) => ({
      jobs: state.jobs.map((job) =>
        job.jobKey === jobKey ? { ...job, state: newState, ...extra } : job,
      ),
    })),

  // -------------------------------------------------------------------------
  // User task lifecycle
  // -------------------------------------------------------------------------

  addUserTask: (task) =>
    set((state) => {
      // Avoid duplicates by jobKey
      if (state.userTasks.some((t) => t.jobKey === task.jobKey)) {
        return state;
      }
      return { userTasks: [...state.userTasks, task] };
    }),

  removeUserTask: (jobKey) =>
    set((state) => ({
      userTasks: state.userTasks.filter((t) => t.jobKey !== jobKey),
      // Clear active task if it was the removed one
      activeTaskJobKey: state.activeTaskJobKey === jobKey ? null : state.activeTaskJobKey,
    })),

  setActiveTask: (jobKey) => set({ activeTaskJobKey: jobKey }),

  // -------------------------------------------------------------------------
  // Event log
  // -------------------------------------------------------------------------

  pushEvent: (event) =>
    set((state) => {
      const events = [event, ...state.events];
      if (events.length > state.maxEvents) {
        events.length = state.maxEvents;
      }
      return { events };
    }),

  clearEvents: () => set({ events: [] }),

  // -------------------------------------------------------------------------
  // Handle raw engine events
  // -------------------------------------------------------------------------

  handleEngineEvent: (workerId, eventType, payload) => {
    const store = get();
    const now = Date.now();

    // Always log the event
    store.pushEvent({
      id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: now,
      workerId,
      eventType,
      payload,
    });

    // Update state based on event type
    switch (eventType) {
      case "ready":
        store.setWorkerStatus(workerId, String(payload.role ?? "unknown"), "ready");
        break;

      case "error":
        store.setWorkerStatus(workerId, "unknown", "error", String(payload.message ?? "Unknown"));
        break;

      case "instanceCreated":
        store.addInstance({
          instanceKey: String(payload.instance_key ?? "unknown"),
          processId: String(payload.process_id ?? payload.process_key ?? "unknown"),
          version: Number(payload.version ?? 1),
          state: "running",
          variables: {},
          startedAt: now,
        });
        if (!payload.instance_key) {
          console.warn("[engineStore] instanceCreated missing 'instance_key' field");
        }
        if (!payload.version) {
          console.warn("[engineStore] instanceCreated missing 'version' field");
        }
        break;

      case "tokenMoved":
        store.updateInstanceElement(String(payload.instance_key), String(payload.element_id));
        break;

      case "jobCreated":
        store.addJob({
          jobKey: String(payload.job_key),
          jobType: String(payload.job_type),
          instanceKey: String(payload.instance_key),
          elementId: String(payload.element_id),
          state: "activatable",
          variables: {},
          customHeaders: {},
          createdAt: now,
        });
        break;

      case "jobActivated":
        store.updateJobState(String(payload.job_key), "activated", {
          workerId: String(payload.worker_id ?? "unknown"),
        });
        if (!payload.worker_id) {
          console.warn("[engineStore] jobActivated missing 'worker_id' field");
        }
        break;

      case "jobCompleted":
        store.updateJobState(String(payload.job_key), "completed", {
          completedAt: now,
        });
        break;

      case "instanceCompleted":
        if (!payload.instance_key) {
          console.warn("[engineStore] instanceCompleted missing 'instance_key' field");
        }
        store.updateInstanceState(String(payload.instance_key ?? "unknown"), "completed");
        break;

      case "incidentRaised":
        store.updateInstanceState(String(payload.instance_key), "failed");
        break;
    }
  },

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  reset: () => set(initialState),
}));
